import { searchTickmarksByRevisionAndElementRequest, createTickmarkRequest, attachTickmarkToElementRequest } from 'api/tickmark-api';
import TickmarkForm from 'models/form/tickmark-form-model';
import { updateElementCacheByCalloutAction } from 'store/actions/element-cache-actions';
import { ELEMENT_ANNOTATIONS_TABS } from 'constants/feature/element-panel-constants';


/**
 * Fetches tickmarks for an element based on search criteria
 * @param {Object} elementDetails - Element details containing revisionId and elementId/id
 * @param {string} search - Search term for tickmarks
 * @returns {Promise<Object>} API response with matching tickmarks
 * @throws {Error} When elementDetails is missing required properties
 */
export const fetchTickmarksForElement = async (elementDetails, search) => {
  const response = await searchTickmarksByRevisionAndElementRequest({
    revisionId: elementDetails.revisionId,
    elementId: elementDetails.elementId || elementDetails.id,
    search,
  });
  return response;
};


/**
 * Creates a new tickmark for an element
 * @param {Object} elementDetails - Element details to initialize tickmark with
 * @param {string} search - Text content for the new tickmark
 * @param {Function|null} setCurrentElementAnnotationsTab - Optional function to set annotation tab (for element panel)
 * @returns {Promise<Object>} API response with created tickmark
 * @throws {Error} When elementDetails is invalid or API call fails
 */
export const createNewTickmarkForElement = async (elementDetails, search, setCurrentElementAnnotationsTab = null) => {
  const response = await createTickmarkRequest({
    tickmark: new TickmarkForm()
      .initFromElement({ elementDetails })
      .setText(search),
  });
  
  if (setCurrentElementAnnotationsTab) {
    setCurrentElementAnnotationsTab(ELEMENT_ANNOTATIONS_TABS.TICKMARK);
  }
  
  return response;
};


/**
 * Attaches a tickmark to an element with context-aware handling
 * @param {Object} tickmark - Tickmark object to attach (will not be mutated)
 * @param {Object} elementDetails - Element details containing revisionId and elementId/id
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function|null} onAttachTickmark - Optional callback for element panel context
 * @param {Function|null} setCurrentElementAnnotationsTab - Optional function to set annotation tab
 * @returns {Promise<void>}
 * @throws {Error} When required parameters are missing or API calls fail
 */
export const attachTickmarkToElementWithHandlers = async (
  tickmark, 
  elementDetails, 
  dispatch, 
  onAttachTickmark = null, 
  setCurrentElementAnnotationsTab = null
) => {
  // Create a new tickmark object to avoid side effects
  const tickmarkToAttach = {
    ...tickmark,
    revisionId: elementDetails.revisionId,
    elementId: elementDetails.elementId || elementDetails.id
  };
  
  if (onAttachTickmark) {
    // Element panel context - use provided callback
    await onAttachTickmark({ tickmark: tickmarkToAttach });
    if (setCurrentElementAnnotationsTab) {
      setCurrentElementAnnotationsTab(ELEMENT_ANNOTATIONS_TABS.TICKMARK);
    }
  } else {
    // Quick action context - direct API call
    const elementId = elementDetails.elementId || elementDetails.id;
    const revisionId = elementDetails.revisionId;
    
    if (!revisionId) {
      throw new Error('Cannot attach tickmark: revisionId is required');
    }
    
    if (!elementId) {
      throw new Error('Cannot attach tickmark: elementId is required');
    }
    
    // Use static imports instead of dynamic imports
    await attachTickmarkToElementRequest({
      tickmarkId: tickmarkToAttach.tickmarkId,
      elementId: elementId,
      revisionId: revisionId,
    });
    
    // Update element cache to show the tickmark
    dispatch(updateElementCacheByCalloutAction({
      elementIds: [elementId],
      showCallout: true,
    }));
  }
};

/**
 * Creates handlers for ConfirmOverrideSystemFlag modal with context-aware behavior
 * @param {Object} elementDetails - Element details object
 * @param {Function|null} dispatch - Redux dispatch function (required for quick actions)
 * @param {Function|null} onAttachTickmark - Optional callback for element panel context
 * @param {Function|null} setCurrentElementAnnotationsTab - Optional function to set annotation tab
 * @returns {Object} Object containing _fetchTickmarks, _createNewTickmark, and onTickmarkAttach handlers
 * @throws {Error} When elementDetails is null/undefined in handler functions
 */
export const createConfirmOverrideSystemFlagHandlers = (
  elementDetails, 
  dispatch, 
  onAttachTickmark = null, 
  setCurrentElementAnnotationsTab = null
) => {
  const _fetchTickmarks = async ({ search }) => {
    if (!elementDetails) {
      return;
    }
    return await fetchTickmarksForElement(elementDetails, search);
  };

  const _createNewTickmark = async (search) => {
    if (!elementDetails) {
      return;
    }
    return await createNewTickmarkForElement(elementDetails, search, setCurrentElementAnnotationsTab);
  };

  const onTickmarkAttachHandler = async ({ tickmark }) => {
    if (!elementDetails) {
      return;
    }
    await attachTickmarkToElementWithHandlers(
      tickmark, 
      elementDetails, 
      dispatch, 
      onAttachTickmark, 
      setCurrentElementAnnotationsTab
    );
  };

  return {
    _fetchTickmarks,
    _createNewTickmark,
    onTickmarkAttach: onTickmarkAttachHandler,
  };
};